import type { RouteRecordRaw } from 'vue-router';
import FullLayout from '@/layouts/full/FullLayout.vue';

// Routes para o sistema de permissões (sincronizadas no DB, sem componente de UI dedicado).
// Cada rota representa um "escopo" da matriz de permissão. O painel /logistica/saldos
// renderiza condicionalmente os elementos com base no userAccessMap.
const phantomScopeMeta = (label: string) => ({
  requiresAuth: true,
  hideFromMenu: true,
  title: `Saldo Estoque · ${label}`,
});

// Rota fantasma do Painel ACD (matriz de permissões em /permissions).
const acdPhantomMeta = (label: string) => ({
  requiresAuth: true,
  hideFromMenu: true,
  title: `Painel ACD · ${label}`,
});

// Rotas fantasma do relatório Saldos por Endereço (escopo por empresa em /permissions).
const saldoEndPhantomMeta = (label: string) => ({
  requiresAuth: true,
  hideFromMenu: true,
  title: `Saldos por Endereço · ${label}`,
});

export const logisticaRoutes: RouteRecordRaw[] = [
  {
    path: '/logistica/transportadora/remessas',
    component: FullLayout,
    meta: { requiresAuth: true, title: 'Remessas - Hub' },
    children: [
      {
        path: '',
        name: 'TransportadoraRemessas',
        component: () => import('@/views/Logistica/Transportadora/Remessas.vue'),
        meta: { title: 'Remessas', breadcrumb: 'Logística / Transportadora / Remessas' },
      },
    ],
  },
  {
    path: '/logistica/saldos',
    component: FullLayout,
    meta: { requiresAuth: true, title: 'Saldo de Estoque - Hub' },
    children: [
      {
        path: '',
        name: 'SaldoEstoquePanel',
        component: () => import('@/views/Logistica/SaldoEstoque/SaldoEstoquePanel.vue'),
        meta: { title: 'Saldo de Estoque', breadcrumb: 'Saldo de Estoque' },
      },
      // Rotas fantasma: existem só para configuração no /permissions (matriz de grupos).
      // O painel principal (acima) verifica userAccessMap[<path>] para decidir o que mostrar.
      { path: 'principal',             name: 'SaldoEstoqueScopePrincipal',    component: () => import('@/views/Logistica/SaldoEstoque/SaldoEstoquePanel.vue'), meta: phantomScopeMeta('Principal') },
      { path: 'principal/valores',     name: 'SaldoEstoqueScopePrincipalValores', component: () => import('@/views/Logistica/SaldoEstoque/SaldoEstoquePanel.vue'), meta: phantomScopeMeta('Principal · Valores') },
      { path: 'secundaria',            name: 'SaldoEstoqueScopeSecundaria',   component: () => import('@/views/Logistica/SaldoEstoque/SaldoEstoquePanel.vue'), meta: phantomScopeMeta('Secundária') },
      { path: 'secundaria/valores',    name: 'SaldoEstoqueScopeSecundariaValores', component: () => import('@/views/Logistica/SaldoEstoque/SaldoEstoquePanel.vue'), meta: phantomScopeMeta('Secundária · Valores') },
      { path: 'exportar',              name: 'SaldoEstoqueScopeExport',       component: () => import('@/views/Logistica/SaldoEstoque/SaldoEstoquePanel.vue'), meta: phantomScopeMeta('Exportar relatório') },
      { path: 'relatorios/principal',  name: 'SaldoEstoqueScopeRelPrincipal', component: () => import('@/views/Logistica/SaldoEstoque/SaldoEstoquePanel.vue'), meta: phantomScopeMeta('Relatórios · Principal') },
      { path: 'relatorios/secundaria', name: 'SaldoEstoqueScopeRelSecundaria', component: () => import('@/views/Logistica/SaldoEstoque/SaldoEstoquePanel.vue'), meta: phantomScopeMeta('Relatórios · Secundária') },
      {
        path: 'admin/sync',
        name: 'SaldoEstoqueProtheusIntegration',
        component: () => import('@/views/Logistica/SaldoEstoque/ProtheusIntegration.vue'),
        meta: { requiresAuth: true, title: 'Integração Protheus', breadcrumb: 'Integração Protheus' },
      },
    ],
  },

  // ── Logística > Relatórios > Saldos por Endereço (MATA226 live) ──
  // As 3 rotas são REAIS de propósito: o syncFrontendRoutes (login do
  // Admin Master) cadastra cada path na tabela `routes`, e o backend
  // verifica a permissão por empresa nos paths /via e /sim. A mesma
  // view atende as 3; meta.tenant pré-seta o switcher.
  {
    path: '/logistica/relatorios/saldos-endereco',
    component: FullLayout,
    meta: { requiresAuth: true, title: 'Saldos por Endereço - Hub' },
    children: [
      {
        path: '',
        name: 'SaldosEndereco',
        component: () => import('@/views/Logistica/Relatorios/SaldosEndereco.vue'),
        meta: {
          requiresAuth: true,
          title: 'Saldos por Endereço',
          breadcrumb: 'Logística / Relatórios / Saldos por Endereço',
        },
      },
      {
        path: 'principal',
        name: 'SaldosEnderecoPrincipal',
        component: () => import('@/views/Logistica/Relatorios/SaldosEndereco.vue'),
        meta: { ...saldoEndPhantomMeta('Principal'), tenant: 'principal' },
      },
      {
        path: 'secundaria',
        name: 'SaldosEnderecoSecundaria',
        component: () => import('@/views/Logistica/Relatorios/SaldosEndereco.vue'),
        meta: { ...saldoEndPhantomMeta('Secundária'), tenant: 'secundaria' },
      },
    ],
  },

  // ── Painel ACD (Dashboard ACD) — Comando como tela única autenticada.
  // O Kanban é uma rota pública (kiosk) declarada em routes.ts; o Mapa foi
  // descontinuado e agora vive como painel inferior dentro do Comando.
  {
    path: '/logistica/separacao',
    component: FullLayout,
    meta: { requiresAuth: true, title: 'Dashboard ACD · Hub' },
    children: [
      {
        path: '',
        name: 'SeparacaoComando',
        component: () => import('@/views/Logistica/Separacao/SeparacaoComando.vue'),
        meta: { requiresAuth: true, title: 'Dashboard ACD', breadcrumb: 'Dashboard ACD' },
      },
      // Rota fantasma para o gate /admin/sync do backend (sync de rotas em /permissions).
      {
        path: 'admin/sync',
        name: 'SeparacaoAdminSync',
        component: () => import('@/views/Logistica/Separacao/SeparacaoComando.vue'),
        meta: acdPhantomMeta('Admin · Sync'),
      },
    ],
  },
];

export default logisticaRoutes;
